import React, {FC, useMemo} from "react";
import {Font, Document, Page, StyleSheet, Text, View, Image} from "@react-pdf/renderer";
import {Shipment} from "../../../types/models/Shipment.ts";
import {Buyer} from "../../../types/models/Buyer.ts";
import {RoadCarrier} from "../../../types/models/RoadCarrier.ts";
import {Contractor} from "../../../types/models/Contractor.ts";
import {displayDateTime} from "../../../../utils/formatDate.ts";
import {fixedFormat} from "../../../../utils/numberFormat.ts";
import {Provider} from "../../../types/models/Provider.ts";
import {Location} from "../../../../location/types/models/Location.ts";
import {ContractorUser} from "../../../types/models/ContractorUser.ts";
import {ShipmentStateDisplay} from "../../../types/models/ShipmentState.d";
import {textToBase64Barcode} from "../../../../utils/barcode.ts";
import {properties} from "../../../../properties";

Font.register({
    family: "Roboto",
    fonts: [
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf", fontWeight: 300},
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf", fontWeight: 400},
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf", fontWeight: 500},
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf", fontWeight: 600},
    ],
})

const styles = StyleSheet.create({
    title: {
        fontSize: 12,
        marginBottom: 10,
        textAlign: "center",
    },
    page: {
        fontFamily: "Roboto",
        backgroundColor: "#ffffff",
        color: "#424242",
        paddingTop: 20,
        paddingBottom: 30,
        paddingHorizontal: 20,
        boxSizing: "border-box",
    },
    owner: {
        fontSize: 10,
        marginBottom: 10,
        textAlign: "center",
    },
    details: {
        fontSize: 10,
        marginBottom: 10,
        textAlign: "left",
    },
    tableDetails: {
        marginBottom: 10,
    },
    detailsSmall: {
        fontSize: 8,
        marginBottom: 10,
        textAlign: "left",
    },
    buyer: {
        fontSize: 10,
        fontWeight: "bold",
        marginBottom: 10,
        textAlign: "left",
        marginLeft: 30,
    },
    watermark: {
        position: "absolute",
        top: "50%",
        left: 0,
        right: 0,
        alignItems: "center",
        textTransform: 'uppercase',
        transform: "rotate(-45deg)",
        zIndex: -1,
        fontSize: 200,
        fontWeight: "bold",
        letterSpacing: 2,
        color: "rgba(150, 150, 150, 0.3)",
        userSelect: 'none',
    },
    footer: {
        position: 'absolute',
        top: 10,
        left: 40,
        right: 40,
        textAlign: 'center',
        fontSize: 7,
    },
})

const tableStyles = StyleSheet.create({
    table: {
        width: "100%",
        marginBottom: 20,
        borderRadius: 4,
    },
    halfTable: {
        flex: 1,
        padding: 1,
        marginBottom: 20,
        borderRadius: 4,
    },
    row: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#e0e0e0",
        alignItems: "center",
        minHeight: 10,
        flexWrap: "wrap",
        ":not(:last-child)": {
            borderBottom: "none",
        },
    },
    rowBorderless: {
        flexDirection: "row",
        alignItems: "flex-start",
        minHeight: 10,
        flexWrap: "wrap",
    },
    rowHead: {
        flexDirection: "row",
    },
    cell: {
        flex: 1,
        padding: 1,
        fontSize: 8,
        color: "#424242",
        alignItems: "center",
    },
    cellBorder: {
        flex: 1,
        padding: 1,
        fontSize: 8,
        borderBottomWidth: 1,
        borderBottomColor: "#e0e0e0",
        color: "#424242",
        alignItems: "center",
    },
    cellRight: {
        flex: 1,
        padding: 1,
        fontSize: 8,
        color: "#424242",
        alignContent: "flex-end",
        textAlign: "right",
    },
    cellLeft: {
        flex: 1,
        padding: 1,
        fontSize: 8,
        color: "#424242",
        alignContent: "flex-end",
        textAlign: "left",
    },
    cellLeftBig: {
        flex: 1,
        padding: 1,
        fontSize: 10,
        color: "#424242",
        alignContent: "flex-end",
        justifyContent: 'flex-start',
        textAlign: "left",
        alignItems: "flex-start",
        top: 0,
    },
    cellCenter: {
        flex: 1,
        padding: 1,
        fontSize: 8,
        color: "#424242",
        alignContent: "flex-end",
        textAlign: "center",
    },
    cellRightBold: {
        flex: 1,
        padding: 1,
        fontSize: 13,
        color: "#424242",
        alignContent: "flex-end",
        textAlign: "right",
        fontWeight: "bold",
    },
    twentyCell: {
        flex: 0.2,
    },
    thrtyCell: {
        flex: 0.3,
    },
    halfCell: {
        flex: 0.5,
    },
    eightyCell: {
        flex: 0.8,
    },
    headerCell: {
        backgroundColor: "#DAF7A6",
        fontWeight: "bold",
        overflow: "hidden",
        whiteSpace: "nowrap",
        alignItems: "center",
    },
    headerCellRight: {
        backgroundColor: "#DAF7A6",
        fontWeight: "bold",
        overflow: "hidden",
        whiteSpace: "nowrap",
        alignItems: "center",
        textAlign: "right",
    },
    headerCellCenter: {
        backgroundColor: "#DAF7A6",
        fontWeight: "bold",
        overflow: "hidden",
        whiteSpace: "nowrap",
        alignItems: "center",
        textAlign: "center",
    },
    totalCell: {
        backgroundColor: "lightgray",
    },
})

export interface IAccTableDataProps {
    sum: number,
    count: number,
    items: Array<ITableDataProps>,
}

export type ITableDataProps = {
    timberTypeName: string;
    className: string;
    thicknessClassName: string;
    totalVolume: number;
};

type ICordwoodListPdfProps = {
    shipment: Shipment;
    sourceLocation: Location | undefined;
    contractor: Contractor | undefined,
    provider: Provider;
    buyer: Buyer;
    roadCarrier: RoadCarrier | undefined;
    contractorUser: ContractorUser | undefined;
    list: { items: [], totalItems: number, totalSum: number };
    numberOfCopies: number;
};

const CordwoodListPdf: FC<ICordwoodListPdfProps> = ({
                                                    shipment,
                                                    sourceLocation,
                                                    contractor,
                                                    provider,
                                                    buyer,
                                                    roadCarrier,
                                                    contractorUser,
                                                    list,
                                                    numberOfCopies,
                                                }) => {

    const draft = shipment.state == ShipmentStateDisplay.InProgress || shipment.state == ShipmentStateDisplay.Upcoming
    const barcodeData = useMemo(() => textToBase64Barcode(`${properties.webAppDomain}/dashboard/cuttings/${shipment.cuttingId}/shipments/${shipment.id}`), [shipment])

    return (
        <Document title={shipment.key}>
            { [...Array(numberOfCopies).keys()].map(i => i + 1).map((pagePosition) => (
            <Page size="A4" style={styles.page} key={pagePosition}>
                <View>

                    { draft &&
                        <View fixed style={styles.watermark}>
                            <Text>DRAFT</Text>
                        </View>
                    }

                    <View style={tableStyles.rowBorderless}>
                        <View style={[styles.title, tableStyles.eightyCell]}>
                            <View style={styles.title}>
                                <Text>TERETNI LIST (POPRATNICA) ZA OBLO DRVO</Text>
                                <Text>No: {shipment.reportKey} ({shipment.key})</Text>
                            </View>
                            <View style={styles.details}>
                                <Text>Lincencirani izvodač: {contractor?.name ?? '-'}</Text>
                                <Text>Otpremio: {contractorUser?.name ?? '-'}</Text>
                                <Text>Datum i vrijeme otpreme: {shipment.shipmentAt ? displayDateTime(shipment.shipmentAt) : '-'}</Text>
                                <Text>Na temelju (radni nalog, klasa zahtjeva): {shipment.workOrder ?? '-'}</Text>
                            </View>
                        </View>
                        <View style={tableStyles.twentyCell}>
                            <Image src={barcodeData} />
                        </View>
                    </View>
                    <View style={tableStyles.table}>
                        <View style={tableStyles.rowBorderless}>
                            <View style={tableStyles.cellLeftBig}>
                                <Text style={styles.details}>MJESTO UTOVARA</Text>
                                <View style={styles.tableDetails}>
                                    <Text>Vlasnik/posjednik:</Text>
                                    <Text>{provider?.name}</Text>
                                    <Text>{provider?.addressIdAddress?.address ?? '-'}, {provider?.addressIdAddress?.city ?? '-'}</Text>
                                </View>
                                <View style={styles.tableDetails}>
                                    <Text>Skladište: {sourceLocation ? sourceLocation.hierarchyName : '-'} {shipment.sourceParcel ? ` > ${shipment.sourceParcel}` : ''}</Text>
                                    <Text>{sourceLocation?.address ? `${sourceLocation.address}, ${sourceLocation.city}`  : ''}</Text>
                                </View>
                                <View style={styles.tableDetails}>
                                    <Text>Prijevoznik: {roadCarrier?.name ?? '-'}</Text>
                                    <Text>Vozač: {shipment.truckerName ?? '-'}</Text>
                                    <Text>Registarska oznaka: {shipment.truckRegistrationNumber ?? '-'}</Text>
                                </View>
                            </View>
                            <View style={tableStyles.cellLeftBig}>
                                <Text style={styles.details}>MJESTO ISTOVARA</Text>
                                <View style={styles.tableDetails}>
                                    <Text>Kupac: {buyer?.name ?? '-'}</Text>
                                    <Text>{buyer?.addressIdAddress?.address ?? '-'}, {buyer?.addressIdAddress?.city ?? '-'}</Text>
                                    <Text>Skladište:</Text>
                                    <Text>{shipment?.targetAddress ?? ''}</Text>
                                    <Text>{shipment?.targetCity ?? ''}</Text>
                                    <Text>{shipment?.targetCountry ?? ''}</Text>
                                </View>
                                <Text>Uplatnica/Nalog/Dražba: {shipment.shipmentOrder ?? '-'}</Text>
                            </View>
                        </View>
                    </View>

                    <View style={styles.detailsSmall}>
                        <Text>Drvo: iz šume. Drvni sortimenti su: {shipment.forPersonalUse ? 'za vlastite potrebe' : 'za prodaju'}</Text>
                    </View>

                    <View wrap={true}>
                        <View style={tableStyles.table}>
                            {/* Table Headers */}
                            <View style={[tableStyles.rowHead, {justifyContent: "space-between"}]}>
                                <Text key="Number" style={[tableStyles.cellRight, tableStyles.headerCell]}>#</Text>
                                <Text key="Vrsta drveta" style={[tableStyles.cell, tableStyles.headerCell]}>Vrsta drveta</Text>
                                <Text key="Sortiment" style={[tableStyles.cell, tableStyles.headerCell]}>Sortiment</Text>
                                <Text
                                    key="Prostorno drvo"
                                    style={[tableStyles.cell, tableStyles.headerCellCenter, {flex: 3}]}
                                >
                                    Prostorno drvo
                                </Text>
                            </View>
                            <View style={[tableStyles.rowHead, {justifyContent: "space-between"}]}>
                                <Text key="Number" style={[tableStyles.cellRight, tableStyles.headerCell]}>&nbsp;</Text>
                                <Text key="Vrsta drveta" style={[tableStyles.cell, tableStyles.headerCell]}>&nbsp;</Text>
                                <Text key="Sortiment" style={[tableStyles.cell, tableStyles.headerCell]}>&nbsp;</Text>
                                <Text key="Prostorno drvo m3" style={[tableStyles.cellRight, tableStyles.headerCellRight]}>m&#xB3;</Text>
                                <Text key="Prostorno drvo prm" style={[tableStyles.cellRight, tableStyles.headerCellRight]}>prm</Text>
                                <Text key="Prostorno drvo npm" style={[tableStyles.cellRight, tableStyles.headerCellRight]}>npm</Text>
                            </View>
                            {list.items.map((row, index) => (
                                <View key={row.id}>
                                    <View style={tableStyles.row}>
                                        <Text style={tableStyles.cellRight}>{index + 1}.</Text>
                                        <Text style={tableStyles.cell}>{row.timberType?.name}</Text>
                                        <Text style={tableStyles.cell}>{row.timberClass?.name}</Text>
                                        { row.measurementType === 'm3' ? <Text style={tableStyles.cellRight}>{fixedFormat(row.volume)}</Text> : <Text style={tableStyles.cellRight}></Text> }
                                        { row.measurementType === 'prm' ? <Text style={tableStyles.cellRight}>{fixedFormat(row.volume)}</Text> : <Text style={tableStyles.cellRight}></Text> }
                                        { row.measurementType === 'npm' ? <Text style={tableStyles.cellRight}>{fixedFormat(row.volume)}</Text> : <Text style={tableStyles.cellRight}></Text> }
                                    </View>
                                </View>
                            ))}
                            <View style={tableStyles.row}>
                                <Text style={[tableStyles.cell, tableStyles.totalCell]}>&nbsp;</Text>
                                <Text style={[tableStyles.cell, tableStyles.totalCell]}>UKUPNO</Text>
                                <Text style={[tableStyles.cell, tableStyles.totalCell]}>VOLUMEN:</Text>
                                <Text style={[tableStyles.cellRight, tableStyles.totalCell]}>{fixedFormat(list.sumM3)}</Text>
                                <Text style={[tableStyles.cellRight, tableStyles.totalCell]}>{fixedFormat(list.sumPrm)}</Text>
                                <Text style={[tableStyles.cellRight, tableStyles.totalCell]}>{fixedFormat(list.sumNpm)}</Text>
                            </View>
                        </View>
                    </View>
                    <View wrap={true}>
                        <View>
                            <View style={tableStyles.rowBorderless}>
                                <Text>&nbsp;</Text>
                            </View>
                            <View style={tableStyles.rowBorderless}>
                                <Text style={tableStyles.cellRight}>Potpis otpremnika:</Text>
                                <Text style={tableStyles.cellBorder}></Text>
                                <Text style={tableStyles.cellCenter}>Žig faksimil</Text>
                                <Text style={tableStyles.cellRight}>Potpis vozača:</Text>
                                <Text style={tableStyles.cellBorder}></Text>
                            </View>
                            <View style={tableStyles.rowBorderless}>
                                <Text>&nbsp;</Text>
                            </View>
                            <View style={tableStyles.rowBorderless}>
                                <Text style={tableStyles.cellRight}>Potpis vlasnika:</Text>
                                <Text style={tableStyles.cellBorder}></Text>
                                <Text style={tableStyles.cellCenter}></Text>
                                <Text style={tableStyles.cellRight}>Primio:</Text>
                                <Text style={tableStyles.cellBorder}></Text>
                            </View>
                        </View>
                    </View>
                </View>
                { numberOfCopies > 1 ?
                    <View fixed>
                        <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                            `Stranica ${pageNumber - (pagePosition - 1) * (totalPages / numberOfCopies)} / ${totalPages / numberOfCopies} | Kopija ${pagePosition} / ${numberOfCopies} | Popratnica No: ${shipment.reportKey} (${shipment.key})`
                        )} />
                    </View>
                    :
                    <View fixed>
                        <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                            `Stranica ${pageNumber - (pagePosition - 1) * (totalPages / numberOfCopies)} / ${totalPages / numberOfCopies} | Popratnica No: ${shipment.reportKey} (${shipment.key})`
                        )} />
                    </View>
                }
            </Page>)
            )}
        </Document>
    )
}

export default CordwoodListPdf
